// steps/Step1.js
import React, { useEffect, useRef, useState } from "react";
import { FaUser, FaBuilding } from "react-icons/fa";
import "../tutorRegistration/TutorRegistration.css";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useGoogleMaps } from "../../context/GoogleMapsProvider";
import LocationInput from "../../helper/locationInput";
import { toast } from "react-hot-toast";
import api from "../../utils/api";
import { validateFile } from "../../utils/fileValidation";

const Step1 = ({
  formData,
  setFormData,
  setErrors,
  errors,
  handleInputChange,
  handlePictureChange,
  handleFileChange,
  registrationType = "tutor",
  userName = "",
  academyName = "",
}) => {
  const { isLoaded, loadError } = useGoogleMaps();
  const autocompleteRef = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [inputValue, setInputValue] = useState(formData.location.address || "");
  const [ninStatus, setNinStatus] = useState(
    formData.ninVerification?.status || "idle",
    // idle | loading | verified | failed
  );
  const fileInputRef = useRef(null);

  // console.log("is loaded", isLoaded, "loadError:", loadError);
  // console.log("form data", formData);

  const navigate = useNavigate();
  const { user, updateUser } = useAuth();

  const isAcademy = registrationType === "academy";

  useEffect(() => {
    // Scroll to top smoothly when the component mounts / route changes
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant", // or "smooth" if you prefer a gentle scroll
    });
  }, []);

  useEffect(() => {
    if (!isLoaded) return;

    const ac = autocompleteRef.current;
    if (!ac) return;

    const handleSelect = async (event) => {
      // console.log("gmp-select fired!", event.detail); // ← debug: must see this in console when selecting

      const { placePrediction } = event.detail || {};
      if (!placePrediction) {
        console.warn("No placePrediction in event");
        return;
      }

      try {
        const place = placePrediction.toPlace();
        await place.fetchFields({
          fields: ["formattedAddress", "displayName", "location", "id"],
        });

        const address = place.formattedAddress || place.displayName || "";

        setFormData((prev) => ({
          ...prev,
          location: {
            address,
            placeId: place.id || "",
            lat: place.location?.lat || 0,
            lng: place.location?.lng || 0,
          },
        }));

        // console.log("Updated location:", {
        //   address,
        //   lat: place.location?.lat,
        //   lng: place.location?.lng,
        // });

        // Clear error if any
        setErrors((prev) => {
          const { address, ...rest } = prev;
          return rest;
        });
      } catch (err) {
        console.error("Place fetch failed:", err);
      }
    };

    ac.addEventListener("gmp-select", handleSelect);

    return () => {
      ac.removeEventListener("gmp-select", handleSelect);
    };
  }, [isLoaded]);

  const countWords = (text) =>
    text
      .trim()
      .split(/\s+/)
      .filter((w) => w.length > 0).length;

  const handleVerifyNin = async () => {
    const nin = (formData.nin || "").trim();

    if (!/^\d{11}$/.test(nin)) {
      setErrors((prev) => ({ ...prev, nin: "Enter a valid 11-digit NIN" }));
      return;
    }

    setNinStatus("loading");
    setErrors((prev) => {
      const { nin: _, ...rest } = prev;
      return rest;
    });

    try {
      const { data } = await api.post("/users/verify-nin", { nin });

      if (data.success) {
        setNinStatus("verified");
        setFormData((prev) => ({
          ...prev,
          ninVerification: {
            status: "found",
            firstName: data.firstName,
            lastName: data.lastName,
            dob: data.dob,
            lookedUpAt: new Date().toISOString(),
          },
        }));
      } else {
        setNinStatus("failed");
        setErrors((prev) => ({
          ...prev,
          nin: data.message || "NIN could not be verified. Check and retry.",
        }));
      }
    } catch (err) {
      const message =
        err?.response?.data?.message || "Network error. Please try again.";
      setNinStatus("failed");
      setErrors((prev) => ({ ...prev, nin: message }));
    }
  };

  const bioWordCount = formData.bio?.trim() ? countWords(formData.bio) : 0;

  const handleInputChangeLocal = (e) => {
    const query = e.target.value;
    setInputValue(query);
    setFormData((prev) => ({
      ...prev,
      location: { ...prev.location, address: query }, // update on every type for fallback
    }));

    if (!isLoaded || query.length < 3) {
      setSuggestions([]);
      return;
    }

    // Safe access to google
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      console.warn("Google Maps API not fully loaded yet");
      return;
    }

    const fetchSuggestions = async () => {
      try {
        const { suggestions: preds } =
          await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
            {
              input: query,
              // locationBias: {
              //   circle: {
              //     center: { lat: 6.3400, lng: 5.6170 }, // Benin City center
              //     radius: 50000 // ~1500km radius, covers Nigeria well
              //   }
              // },
              origin: { lat: 6.34, lng: 5.617 },
              region: "ng", // optional: bias toward Nigeria
              language: "en",
            },
          );

        setSuggestions(preds || []);
      } catch (err) {
        console.error("Autocomplete suggestions failed:", err);
        setSuggestions([]);
      }
    };

    fetchSuggestions();
  };

  const academyStates = [
    "lagos",
    "abuja",
    "rivers",
    "kano",
    "oyo",
    "kaduna",
    "katsina",
  ];

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const error = validateFile(file, { maxSizeBytes: 2 * 1024 * 1024 });
    if (error) {
      toast.error(error);
      e.target.value = "";
      return;
    }

    setFormData({ ...formData, academyLogo: file });
    if (errors.academyLogo)
      setFormData((prev) => ({
        ...prev,
        errors: { ...prev.errors, academyLogo: "" },
      }));
  };

  // No validation existed here at all — the ID document is the field most
  // likely to be a phone-camera photo (HEIC on iPhone), so this was the
  // single biggest source of unexplained "Profile Failed to Save" reports:
  // the file passed the browser's picker silently, then failed the
  // backend's multer fileFilter after a full upload round-trip.
  const handleIdChange = (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    const error = validateFile(file, { maxSizeBytes: 10 * 1024 * 1024 });
    if (error) {
      toast.error(error);
      e.target.value = "";
      return;
    }

    setFormData({ ...formData, [field]: file });
    if (errors[field])
      setFormData((prev) => ({
        ...prev,
        errors: { ...prev.errors, [field]: "" },
      }));
  };

  const getSocialPlaceholder = (platform) => {
    switch (platform) {
      case "linkedin":
        return "https://linkedin.com/in/yourname";
      case "instagram":
        return "https://instagram.com/yourusername";
      case "x":
        return "https://x.com/yourusername or @yourusername";
      case "facebook":
        return "https://facebook.com/yourpage";
      case "youtube":
        return "https://youtube.com/@yourchannel";
      default:
        return "Enter your profile link";
    }
  };

  const getExampleLink = (platform) => {
    switch (platform) {
      case "linkedin":
        return "https://linkedin.com/in/olatunji-tutor";
      case "instagram":
        return "https://instagram.com/olatunji_tutor";
      case "x":
        return "https://x.com/olatunjitutor";
      case "facebook":
        return "https://facebook.com/olatunjitutorpage";
      case "youtube":
        return "https://youtube.com/@olatunjitutor";
      default:
        return "";
    }
  };

  const renderCommonFields = () => (
    <>
      <LocationInput formData={formData} setFormData={setFormData} />

      {errors.address && <p className="error">{errors.address}</p>}
      <p className="note" style={{ marginBottom: "16px" }}>
        Type street, area, or city — suggestions will appear
      </p>

      {/* <div
        className="form-group"
        style={{ position: "relative", maxWidth: "100%" }}
      >
        <label>Bio</label>
        <textarea
          name="bio"
          rows="5"
          cols="50"
          placeholder="Introduce yourself..."
          value={formData.bio || ""}
          onChange={handleInputChange}
          style={{
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        />
        {errors.bio && <p className="error">{errors.bio}</p>}
        <p className="note">
          {isAcademy
            ? "Your training center bio should emphasize your professional licenses, certifications, and qualifications, as well as any notable achievements of the center."
            : "Your bio should highlight your professional licenses, certifications, and qualifications."}
        </p>
      </div> */}

      <div
        className="form-group"
        style={{ position: "relative", maxWidth: "100%" }}
      >
        <label>Bio</label>

        <textarea
          name="bio"
          rows="5"
          cols="50"
          placeholder="Introduce yourself..."
          value={formData.bio || ""}
          onChange={handleInputChange}
          style={{
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            borderColor:
              bioWordCount > 0 && bioWordCount < 75
                ? "var(--color-danger, #e24b4a)"
                : bioWordCount >= 75 && bioWordCount <= 150
                  ? "var(--color-success, #3b6d11)"
                  : undefined,
          }}
        />

        {/* Word counter */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "6px",
          }}
        >
          <p className="note" style={{ margin: 0 }}>
            {isAcademy
              ? "Your training center bio should emphasize your professional licenses, certifications, and qualifications, as well as any notable achievements of the center."
              : "Your bio should highlight your professional licenses, certifications, and qualifications."}
          </p>
          <span
            style={{
              fontSize: "12px",
              whiteSpace: "nowrap",
              marginLeft: "12px",
              fontWeight: 500,
              color:
                bioWordCount === 0
                  ? "var(--color-text-secondary, #888)"
                  : bioWordCount < 75
                    ? "#e24b4a"
                    : bioWordCount <= 150
                      ? "#3b6d11"
                      : "#e24b4a",
            }}
          >
            {bioWordCount} / 150 words
          </span>
        </div>

        {/* Inline hint below counter */}
        {bioWordCount > 0 && bioWordCount < 75 && (
          <p className="note" style={{ color: "#e24b4a", marginTop: "4px" }}>
            {75 - bioWordCount} more word{75 - bioWordCount !== 1 ? "s" : ""}{" "}
            needed (minimum 75)
          </p>
        )}
        {bioWordCount > 150 && (
          <p className="note" style={{ color: "#e24b4a", marginTop: "4px" }}>
            {bioWordCount - 150} word{bioWordCount - 150 !== 1 ? "s" : ""} over
            the 150-word limit
          </p>
        )}

        {errors.bio && <p className="error">{errors.bio}</p>}
      </div>

      <div className="form-group">
        <label>Social Media Profile (Optional)</label>

        <select
          name="socialMedia.platform"
          value={formData.socialMedia?.platform || ""}
          onChange={handleInputChange}
          className="tutor-input-field"
        >
          <option value="">Select platform (optional)</option>
          <option value="linkedin">LinkedIn</option>
          <option value="instagram">Instagram</option>
          <option value="x">X (Twitter)</option>
          <option value="facebook">Facebook</option>
          <option value="youtube">YouTube</option>
        </select>

        {formData.socialMedia?.platform && (
          <div style={{ marginTop: "12px" }}>
            <input
              type="text"
              name="socialMedia.link"
              value={formData.socialMedia?.link || ""}
              onChange={handleInputChange}
              placeholder={getSocialPlaceholder(formData.socialMedia.platform)}
              className="tutor-input-field"
            />
          </div>
        )}

        <p className="note">
          Tooltips: Choose the social media platform you'd like to add and
          provide the corresponding link.
        </p>

        {errors.socialMedia?.platform && (
          <p className="error">{errors.socialMedia.platform}</p>
        )}
        {errors.socialMedia?.link && (
          <p className="error">{errors.socialMedia.link}</p>
        )}
      </div>
    </>
  );

  const renderTutorFields = () => (
    <>
      <div className="form-group">
        <label>Upload Profile Picture *</label>
        <div className="avatar-container">
          <div className="avatar-placeholder">
            {formData.picture ? (
              <img
                src={URL.createObjectURL(formData.picture)}
                alt="Preview"
                className="avatar-preview"
              />
            ) : user?.picture?.url ? (
              <img
                src={user.picture.url}
                alt="Current profile"
                className="avatar-preview"
              />
            ) : (
              <FaUser className="default-avatar" />
            )}
          </div>
          <input
            type="file"
            accept="image/*"
            onChange={handlePictureChange}
            className="file-input"
            id="picture-input"
            name="picture"
          />
          <label htmlFor="picture-input" className="file-label">
            <span className="select-file">
              {user?.picture?.url ? "Change image" : "Select an image"}
            </span>
            <span className="select-text">Make sure the file is below 2mb</span>
          </label>
        </div>
        {errors.picture && <p className="error">{errors.picture}</p>}
        <p className="note">
          Kindly upload a clearer picture, your image should be a professional
          image as it would be displayed to the public
        </p>
      </div>

      <div
        className="form-group"
        style={{ position: "relative", maxWidth: "100%" }}
      >
        <label>Gender</label>
        <select
          name="gender"
          value={formData.gender}
          onChange={handleInputChange}
          className="tutor-input-field"
          style={{
            // width: "100%",
            // maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          <option value="">Select Gender</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
        </select>
        {errors.gender && <p className="error">{errors.gender}</p>}
      </div>

      {/* ── NIN Verification ── */}
      {/* <div className="form-group">
        <label>National Identification Number (NIN) *</label>

        <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
          <input
            type="text"
            name="nin"
            maxLength={11}
            placeholder="Enter your 11-digit NIN"
            value={formData.nin || ""}
            onChange={(e) => {
              handleInputChange(e);
              if (ninStatus !== "idle") setNinStatus("idle"); // reset on edit
            }}
            className="tutor-input-field"
            style={{
              flex: 1,
              borderColor:
                ninStatus === "verified"
                  ? "var(--color-success, #3b6d11)"
                  : ninStatus === "failed"
                    ? "var(--color-danger, #e24b4a)"
                    : undefined,
            }}
            disabled={ninStatus === "verified"}
          />

          <button
            type="button"
            onClick={handleVerifyNin}
            disabled={ninStatus === "loading" || ninStatus === "verified"}
            style={{
              padding: "10px 16px",
              background:
                ninStatus === "verified"
                  ? "#3b6d11"
                  : ninStatus === "failed"
                    ? "#e24b4a"
                    : "var(--color-primary)",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              cursor:
                ninStatus === "loading" || ninStatus === "verified"
                  ? "default"
                  : "pointer",
              whiteSpace: "nowrap",
              opacity: ninStatus === "loading" ? 0.7 : 1,
              transition: "background 0.2s",
            }}
          >
            {ninStatus === "loading"
              ? "Checking…"
              : ninStatus === "verified"
                ? "✔ Verified"
                : ninStatus === "failed"
                  ? "Retry"
                  : "Verify NIN"}
          </button>
        </div>

        {ninStatus === "verified" && (
          <p className="note" style={{ color: "#3b6d11", marginTop: "6px" }}>
            ✔ Verified as{" "}
            <strong>
              {formData.ninVerification?.firstName}{" "}
              {formData.ninVerification?.lastName}
            </strong>
            . Make sure your uploaded ID matches this name.
          </p>
        )}

        {errors.nin && <p className="error">{errors.nin}</p>}

        <p className="note">
          Your 11-digit NIN is on your NIN slip, the NIMC mobile app, or dial{" "}
          <strong>*346#</strong> to retrieve it. It is used only for identity
          verification and is never shown publicly.
        </p>
      </div>

      <div className="form-group">
        <label>
          {isAcademy
            ? "Business Registration Document *"
            : "Identity Card (NIN/Passport/Driver License) *"}
        </label>

        <div className="file-row">
          <input
            ref={fileInputRef}
            type="file"
            name="id"
            accept="image/*,application/pdf"
            onChange={(e) =>
              handleIdChange(e, isAcademy ? "businessDoc" : "id")
            }
            className="tutor-input-field"
            required
          />

          {formData[isAcademy ? "businessDoc" : "id"] && (
            <div className="file-icons-outside">
              <span className="file-check">✔</span>

              <span
                className="file-remove"
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    [isAcademy ? "businessDoc" : "id"]: null,
                  }));

                  if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                  }
                }}
              >
                ✖
              </span>
            </div>
          )}
        </div>

        {errors[isAcademy ? "businessDoc" : "id"] && (
          <p className="error">{errors[isAcademy ? "businessDoc" : "id"]}</p>
        )}

        <p className="note">
          {isAcademy
            ? "Upload CAC certificate or business registration document"
            : "Upload valid government ID for verification"}
        </p>
      </div> */}

      {/* ── Identity / Business Document ── */}
      {isAcademy ? (
        /* ── ACADEMY: CAC / Business registration doc only ── */
        <div className="form-group">
          <label>Business Registration Document *</label>
          <div className="file-row">
            <input
              ref={fileInputRef}
              type="file"
              name="businessDoc"
              accept="image/*,application/pdf"
              onChange={(e) => handleIdChange(e, "businessDoc")}
              className="tutor-input-field"
            />
            {formData.businessDoc && (
              <div className="file-icons-outside">
                <span className="file-check">✔</span>
                <span
                  className="file-remove"
                  onClick={() => {
                    setFormData((prev) => ({ ...prev, businessDoc: null }));
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                >
                  ✖
                </span>
              </div>
            )}
          </div>
          {errors.businessDoc && <p className="error">{errors.businessDoc}</p>}
          <p className="note">
            Upload your CAC certificate or business registration document
          </p>
        </div>
      ) : (
        /* ── TUTOR: ID type selector + conditional NIN + doc upload ── */
        <>
          {/* Step 1: pick ID type */}
          <div className="form-group">
            <label>Identity Document Type *</label>
            {/* <select
              name="idType"
              value={formData.idType || ""}
              onChange={(e) => {
                handleInputChange(e);
                // Reset NIN when switching away from NIN
                if (e.target.value !== "nin") {
                  setNinStatus("idle");
                  setFormData((prev) => ({
                    ...prev,
                    nin: "",
                    ninVerification: null,
                  }));
                }
              }}
              className="tutor-input-field"
            >
              <option value="">Select ID type</option>
              <option value="nin">National ID (NIN)</option>
              <option value="passport">International Passport</option>
              <option value="drivers_license">Driver's License</option>
            </select> */}
            <select
              name="idType"
              value={formData.idType || ""}
              onChange={(e) => {
                const val = e.target.value;

                // Update formData directly — don't rely on parent's handleInputChange
                setFormData((prev) => ({
                  ...prev,
                  idType: val,
                  // Reset NIN fields when switching away from NIN
                  ...(val !== "nin" ? { nin: "", ninVerification: null } : {}),
                }));

                // Also reset ninStatus local state
                if (val !== "nin") {
                  setNinStatus("idle");
                }

                // Clear any idType error
                setErrors((prev) => {
                  const { idType, ...rest } = prev;
                  return rest;
                });
              }}
              className="tutor-input-field"
            >
              <option value="">Select ID type</option>
              <option value="nin">National ID (NIN)</option>
              <option value="passport">International Passport</option>
              <option value="drivers_license">Driver's License</option>
            </select>
            {errors.idType && <p className="error">{errors.idType}</p>}
          </div>

          {/* Step 2: NIN input + verify — only when NIN selected */}
          {/* {formData.idType === "nin" && (
            <div className="form-group">
              <label>National Identification Number (NIN) *</label>
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "flex-start",
                }}
              >
                <input
                  type="text"
                  name="nin"
                  maxLength={11}
                  placeholder="Enter your 11-digit NIN"
                  value={formData.nin || ""}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, ""); // digits only
                    setFormData((prev) => ({ ...prev, nin: val }));
                    if (ninStatus !== "idle") setNinStatus("idle");
                    setErrors((prev) => {
                      const { nin, ...rest } = prev;
                      return rest;
                    });
                  }}
                  className="tutor-input-field"
                  style={{
                    flex: 1,
                    borderColor:
                      ninStatus === "verified"
                        ? "var(--color-success, #3b6d11)"
                        : ninStatus === "failed"
                          ? "var(--color-danger, #e24b4a)"
                          : undefined,
                  }}
                  disabled={ninStatus === "verified"}
                />
                <button
                  type="button"
                  onClick={handleVerifyNin}
                  disabled={ninStatus === "loading" || ninStatus === "verified"}
                  className={`nin-verify-btn${ninStatus === "verified" ? " verified" : ninStatus === "failed" ? " failed" : ""}`}
                >
                  {ninStatus === "loading"
                    ? "Checking…"
                    : ninStatus === "verified"
                      ? "✔ Verified"
                      : ninStatus === "failed"
                        ? "Retry"
                        : "Verify NIN"}
                </button>
              </div>

              {ninStatus === "verified" && (
                <p
                  className="note"
                  style={{ color: "#3b6d11", marginTop: "6px" }}
                >
                  ✔ Verified as{" "}
                  <strong>
                    {formData.ninVerification?.firstName}{" "}
                    {formData.ninVerification?.lastName}
                  </strong>
                  . Make sure your uploaded ID matches this name.
                </p>
              )}
              {errors.nin && <p className="error">{errors.nin}</p>}
              <p className="note">
                Your 11-digit NIN is on your NIN slip, NIMC mobile app, or dial{" "}
                <strong>*346#</strong>. Used only for identity verification,
                never shown publicly.
              </p>
            </div>
          )} */}

          {formData.idType === "nin" && (
            <div className="form-group">
              <label>
                National Identification Number (NIN){" "}
                <span style={{ color: "#888", fontWeight: 400 }}>
                  (Optional — verification coming soon)
                </span>
              </label>
              <input
                type="text"
                name="nin"
                maxLength={11}
                placeholder="Enter your 11-digit NIN"
                value={formData.nin || ""}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  setFormData((prev) => ({ ...prev, nin: val }));
                  setErrors((prev) => {
                    const { nin, ...rest } = prev;
                    return rest;
                  });
                }}
                className="tutor-input-field"
              />
              {errors.nin && <p className="error">{errors.nin}</p>}
              <p className="note">
                Your 11-digit NIN is on your NIN slip, NIMC mobile app, or dial{" "}
                <strong>*346#</strong>. 
                {/* NIN verification will be enabled once we receive NDPR certification. */}
              </p>
            </div>
          )}

          {/* Step 3: doc upload — always shown once ID type is picked */}
          {formData.idType && (
            <div className="form-group">
              <label>
                Upload{" "}
                {formData.idType === "nin"
                  ? "NIN Slip / NIMC Card"
                  : formData.idType === "passport"
                    ? "International Passport"
                    : formData.idType === "drivers_license"
                      ? "Driver's License"
                      : "Identity Document"}{" "}
                *
              </label>
              <div className="file-row">
                <input
                  ref={fileInputRef}
                  type="file"
                  name="id"
                  accept="image/*,application/pdf"
                  onChange={(e) => handleIdChange(e, "id")}
                  className="tutor-input-field"
                />
                {formData.id && (
                  <div className="file-icons-outside">
                    <span className="file-check">✔</span>
                    <span
                      className="file-remove"
                      onClick={() => {
                        setFormData((prev) => ({ ...prev, id: null }));
                        if (fileInputRef.current)
                          fileInputRef.current.value = "";
                      }}
                    >
                      ✖
                    </span>
                  </div>
                )}
              </div>
              {errors.id && <p className="error">{errors.id}</p>}
              <p className="note">
                {formData.idType === "nin"
                  ? "Upload your NIN slip or NIMC card for visual confirmation"
                  : "Upload a clear photo or scan of your document (max 5MB)"}
              </p>
            </div>
          )}
        </>
      )}

      {renderCommonFields()}
    </>
  );

  const renderAcademyFields = () => (
    <>
      <div className="form-group">
        <label>Upload Training Center Logo *</label>
        <div className="avatar-container">
          <div className="avatar-placeholder academy-logo-placeholder">
            {formData.academyLogo ? (
              <img
                src={URL.createObjectURL(formData.academyLogo)}
                alt="Training Center Logo Preview"
                className="avatar-preview academy-logo-preview"
              />
            ) : user?.picture?.url ? (
              <img
                src={user.picture.url}
                alt="Current logo"
                className="avatar-preview academy-logo-preview"
              />
            ) : (
              <FaBuilding className="default-avatar academy-default-icon" />
            )}
          </div>
          <input
            type="file"
            accept="image/*"
            onChange={handleLogoChange}
            className="file-input"
            id="academy-logo-input"
          />
          <label htmlFor="academy-logo-input" className="file-label">
            <span className="select-file">Select logo image</span>
            <span className="select-text">Make sure the file is below 2mb</span>
          </label>
        </div>
        {errors.academyLogo && <p className="error">{errors.academyLogo}</p>}
        <p className="note">
          Upload your center's official logo. This will be displayed publicly to
          build brand recognition.
        </p>
      </div>

      <div className="form-group">
        <label>Name of Training Center *</label>
        <input
          type="text"
          name="academyName"
          placeholder="Enter full name of your training center"
          value={formData.academyName || user?.academyName || ""}
          onChange={handleInputChange}
          className="tutor-input-field"
          required
        />
        {errors.academyName && <p className="error">{errors.academyName}</p>}
      </div>

      <div className="form-group">
        <label>Training Center Email *</label>
        <input
          type="email"
          name="academyEmail"
          placeholder="trainingcenter@example.com"
          value={formData.academyEmail || user?.email || ""}
          onChange={handleInputChange}
          className="tutor-input-field"
          required
        />
        {errors.academyEmail && <p className="error">{errors.academyEmail}</p>}
        <p className="tooltip">
          Tooltips: Use your official center email for verification and
          communication.
        </p>
      </div>

      {renderCommonFields()}
    </>
  );

  return <>{isAcademy ? renderAcademyFields() : renderTutorFields()}</>;
};

export default Step1;
